import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Menu } from 'lucide-react';
import AdminSidebar from './AdminSidebar';
import DashboardOverview from './DashboardOverview';
import UsersManagement from './Usersmanagement';
import AnalyticsPage from './Analyticspage';
import SubscriptionsPage from './SubscriptionsPage';
import BlogPage from './BlogPage';
import NotificationsPage from './NotificationsPage';
import SecurityPage from './SecurityPage';
import SettingsPage from './SettingsPage';
import ArticlesManager from './ArticlesManager';
import ContentViewer from './ContentViewer';
import PostingCalendar from './PostingCalendar';
import CampaignAnalytics from './CampaignAnalytics';
import ContentStrategy from './ContentStrategy';
import ConnectedAccounts from './ConnectedAccounts';
import ErrorLogsViewer from './ErrorLogsViewer';
import SystemSettings from './SystemSettings';
import SubscribersList from './SubscribersList';
import type { Admin } from './types';

function AdminDashboard() {
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const adminData = localStorage.getItem('adminData');
    if (adminData) {
      try {
        setAdmin(JSON.parse(adminData));
      } catch (error) {
        console.error('Failed to parse admin data:', error);
        navigate('/admin/login');
      }
    }
    setLoading(false);
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminData');
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0d0d0d] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-white/10 border-t-primary rounded-full animate-spin" />
          <p className="text-white/40 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (!admin) return null;

  return (
    <div className="min-h-screen bg-[#0d0d0d] flex" style={{ colorScheme: 'dark' }}>
      {/* Mobile header bar */}
      <div className="fixed top-0 left-0 right-0 z-30 flex items-center gap-3 px-4 py-3 bg-[#111113]/95 backdrop-blur-xl border-b border-white/[0.06] md:hidden">
        <button
          className="text-white/60 hover:text-white transition-colors"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <Menu size={20} />
        </button>
        <h2 className="text-sm font-semibold text-white">Admin Panel</h2>
      </div>

      {/* Sidebar */}
      <AdminSidebar
        admin={admin}
        onLogout={handleLogout}
        mobileOpen={mobileMenuOpen}
        onMobileClose={() => setMobileMenuOpen(false)}
      />

      {/* Main content */}
      <main className="flex-1 min-w-0 overflow-auto pt-14 md:pt-0">
        <div className="p-6 max-w-[1400px] mx-auto">
          <Routes>
            <Route path="/" element={<DashboardOverview />} />
            <Route path="/users" element={<UsersManagement />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/subscriptions" element={<SubscriptionsPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/new" element={<BlogPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/security" element={<SecurityPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/system-settings" element={<SystemSettings />} />
            <Route path="/articles" element={<ArticlesManager />} />
            <Route path="/content" element={<ContentViewer />} />
            <Route path="/calendar" element={<PostingCalendar />} />
            <Route path="/campaigns" element={<CampaignAnalytics />} />
            <Route path="/strategy" element={<ContentStrategy />} />
            <Route path="/connections" element={<ConnectedAccounts />} />
            <Route path="/marketing-analytics" element={<div className="text-white/40 py-20 text-center">Marketing Analytics — Coming Soon</div>} />
            <Route path="/errors" element={<ErrorLogsViewer />} />
            <Route path="/subscribers" element={<SubscribersList />} />
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}

export default AdminDashboard;
